import Ably from "ably";
import { queryKeys } from "@/lib/query-keys";
import { env } from "./env";

// We'll get the queryClient instance from React context instead of importing
// to ensure we use the same instance as the components
let globalQueryClient: any = null;

export const setQueryClient = (client: any) => {
  globalQueryClient = client;
};

// Initialize Ably client
export const ably = new Ably.Realtime({
  key: env.ABLY_API_KEY!,
  clientId: "healthcare-dashboard",
});

// Connection state management
interface ConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  retryCount: number;
}

let connectionState: ConnectionState = {
  isConnected: false,
  isConnecting: false,
  error: null,
  retryCount: 0,
};

// Connection state listeners
const connectionListeners = new Set<(state: ConnectionState) => void>();

export const addConnectionListener = (
  listener: (state: ConnectionState) => void
) => {
  connectionListeners.add(listener);
  return () => connectionListeners.delete(listener);
};

const notifyConnectionListeners = () => {
  connectionListeners.forEach((listener) => listener(connectionState));
};

// Initialize connection monitoring
ably.connection.on(
  ["connected", "disconnected", "failed", "connecting"],
  (stateChange) => {
    const prevState = { ...connectionState };

    switch (stateChange.current) {
      case "connected":
        connectionState = {
          isConnected: true,
          isConnecting: false,
          error: null,
          retryCount: 0,
        };
        break;
      case "connecting":
        connectionState = {
          ...connectionState,
          isConnecting: true,
          error: null,
        };
        break;
      case "disconnected":
      case "failed":
        connectionState = {
          isConnected: false,
          isConnecting: false,
          error: stateChange.reason?.message || "Connection failed",
          retryCount: connectionState.retryCount + 1,
        };
        break;
    }

    if (JSON.stringify(prevState) !== JSON.stringify(connectionState)) {
      notifyConnectionListeners();
    }
  }
);

// Active chat subscriptions for individual conversations
// Map stores: chatId -> { channel, handlers: Set<string> }
interface ChannelSubscription {
  channel: Ably.RealtimeChannel;
  handlers: Set<string>;
}

const activeSubscriptions = new Map<string, ChannelSubscription>();

// Subscription contexts to prevent duplicate event handling
export type SubscriptionContext = "conversation" | "chatlist";

// Debounce map to prevent rapid successive refetches for the same chat
const refetchDebounceMap = new Map<string, NodeJS.Timeout>();
const REFETCH_DEBOUNCE_MS = 500; // Increased to 500ms to prevent rapid loops

// Track active refetches to prevent overlapping calls
const activeRefetches = new Map<string, Promise<void>>();

// Track last refetch time per chat to implement throttling
const lastRefetchTime = new Map<string, number>();
const MIN_REFETCH_INTERVAL_MS = 1000; // Minimum 1 second between refetches

// Retry logic with exponential backoff
const retryWithBackoff = async (
  fn: () => Promise<any>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<any> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        console.error("Max retries reached:", error);
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`Retry attempt ${attempt + 1} after ${delay}ms:`, error);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// Subscribe to a specific chat with context-aware deduplication
export const subscribeToChat = (
  chatId: string,
  context: SubscriptionContext = "conversation",
  callback?: () => void
) => {
  try {
    const channelName = `chat:${chatId}`;
    const handlerId = `${context}_${chatId}`;

    // Get or create channel subscription
    let subscription = activeSubscriptions.get(chatId);

    if (!subscription) {
      // Create new subscription
      const channel = ably.channels.get(channelName);
      subscription = {
        channel,
        handlers: new Set<string>(),
      };
      activeSubscriptions.set(chatId, subscription);
    }

    // Check if this handler is already registered
    if (subscription.handlers.has(handlerId)) {
      return subscription.channel;
    }

    // Message handler with debouncing, throttling, and retry logic
    const handleMessage = async (message: any) => {
      if (!globalQueryClient) return;

      try {
        const debounceKey = `${chatId}_${context}`;
        const now = Date.now();

        // THROTTLE: Check if we refetched too recently
        const lastRefetch = lastRefetchTime.get(debounceKey) || 0;
        if (now - lastRefetch < MIN_REFETCH_INTERVAL_MS) {
          console.log(
            `Throttling refetch for ${debounceKey}, last refetch ${now - lastRefetch}ms ago`
          );
          return; // Skip this event if we refetched within the last second
        }

        // DEBOUNCE: Clear any pending timeout
        if (refetchDebounceMap.has(debounceKey)) {
          clearTimeout(refetchDebounceMap.get(debounceKey));
        }

        // PREVENT OVERLAPPING: Check if already refetching
        if (activeRefetches.has(debounceKey)) {
          console.log(`Skipping overlapping refetch for ${debounceKey}`);
          return; // Don't start a new refetch if one is already in progress
        }

        const debounceTimeout = setTimeout(async () => {
          try {
            // Mark refetch as active
            const refetchPromise = (async () => {
              try {
                // Update last refetch time BEFORE starting to prevent rapid triggers
                lastRefetchTime.set(debounceKey, Date.now());

                // Handle different message types
                if (
                  message.name === "message:read" ||
                  message.data?.type === "message:read"
                ) {
                  // Refetch chat list and detail with retry
                  await retryWithBackoff(async () => {
                    await Promise.all([
                      globalQueryClient.refetchQueries({
                        queryKey: queryKeys.ping.chats.all(),
                      }),
                      globalQueryClient.refetchQueries({
                        queryKey: queryKeys.ping.chats.detail(chatId),
                      }),
                    ]);
                  });
                  return;
                }

                // For new messages, refetch everything with retry
                await retryWithBackoff(async () => {
                  await Promise.all([
                    // Refetch infinite messages query
                    globalQueryClient.refetchQueries({
                      queryKey: queryKeys.ping.messages.infinite(chatId),
                      exact: false, // Match all pageSize variants
                    }),
                    // Refetch chat list
                    globalQueryClient.refetchQueries({
                      queryKey: queryKeys.ping.chats.all(),
                    }),
                    // Refetch chat detail
                    globalQueryClient.refetchQueries({
                      queryKey: queryKeys.ping.chats.detail(chatId),
                    }),
                  ]);
                });

                // Call context-specific callback after successful refetch
                if (callback) {
                  callback();
                }
              } finally {
                // Clean up active refetch tracking
                activeRefetches.delete(debounceKey);
              }
            })();

            // Track active refetch
            activeRefetches.set(debounceKey, refetchPromise);
            await refetchPromise;
          } catch (error) {
            console.error(`Error handling message for chat ${chatId}:`, error);
            // Clean up on error
            activeRefetches.delete(debounceKey);
          } finally {
            refetchDebounceMap.delete(debounceKey);
          }
        }, REFETCH_DEBOUNCE_MS);

        refetchDebounceMap.set(debounceKey, debounceTimeout);
      } catch (error) {
        console.error(`Error in message handler for chat ${chatId}:`, error);
      }
    };

    // Subscribe to all relevant message types
    const eventTypes = [
      "message",
      "new_message",
      "message:new",
      "message:read",
    ];
    eventTypes.forEach((eventType) => {
      subscription!.channel.subscribe(eventType, handleMessage);
    });

    // Mark this handler as registered
    subscription.handlers.add(handlerId);

    return subscription.channel;
  } catch (error) {
    console.error(`Error subscribing to chat ${chatId}:`, error);
    throw error;
  }
};

// Unsubscribe from a specific chat context
export const unsubscribeFromChat = (
  chatId: string,
  context?: SubscriptionContext
) => {
  const subscription = activeSubscriptions.get(chatId);
  if (!subscription) return;

  if (context) {
    // Remove specific context handler
    const handlerId = `${context}_${chatId}`;
    subscription.handlers.delete(handlerId);

    // If no more handlers, unsubscribe and remove
    if (subscription.handlers.size === 0) {
      subscription.channel.unsubscribe();
      activeSubscriptions.delete(chatId);
    }
  } else {
    // Unsubscribe entirely from this chat
    subscription.channel.unsubscribe();
    activeSubscriptions.delete(chatId);
  }
};

// Unsubscribe from all chats
export const unsubscribeFromAllChats = () => {
  activeSubscriptions.forEach((subscription) => {
    subscription.channel.unsubscribe();
  });
  activeSubscriptions.clear();
};

// Subscribe to a specific internal chat with context-aware deduplication
export const subscribeToInternalChat = (
  chatId: string,
  context: SubscriptionContext = "conversation",
  callback?: () => void
) => {
  try {
    const channelName = `internal-chat:${chatId}`;
    const handlerId = `${context}_${chatId}`;

    // Get or create channel subscription
    let subscription = activeSubscriptions.get(`internal_${chatId}`);

    if (!subscription) {
      // Create new subscription
      const channel = ably.channels.get(channelName);
      subscription = {
        channel,
        handlers: new Set<string>(),
      };
      activeSubscriptions.set(`internal_${chatId}`, subscription);
    }

    // Check if this handler is already registered
    if (subscription.handlers.has(handlerId)) {
      return subscription.channel;
    }

    // Message handler with debouncing, throttling, and retry logic
    const handleMessage = async (message: any) => {
      if (!globalQueryClient) return;

      try {
        const debounceKey = `internal_${chatId}_${context}`;
        const now = Date.now();

        // THROTTLE: Check if we refetched too recently
        const lastRefetch = lastRefetchTime.get(debounceKey) || 0;
        if (now - lastRefetch < MIN_REFETCH_INTERVAL_MS) {
          console.log(
            `Throttling refetch for ${debounceKey}, last refetch ${now - lastRefetch}ms ago`
          );
          return; // Skip this event if we refetched within the last second
        }

        // DEBOUNCE: Clear any pending timeout
        if (refetchDebounceMap.has(debounceKey)) {
          clearTimeout(refetchDebounceMap.get(debounceKey));
        }

        // PREVENT OVERLAPPING: Check if already refetching
        if (activeRefetches.has(debounceKey)) {
          console.log(`Skipping overlapping refetch for ${debounceKey}`);
          return; // Don't start a new refetch if one is already in progress
        }

        const debounceTimeout = setTimeout(async () => {
          try {
            // Mark refetch as active
            const refetchPromise = (async () => {
              try {
                // Update last refetch time BEFORE starting to prevent rapid triggers
                lastRefetchTime.set(debounceKey, Date.now());

                // Handle different message types
                if (
                  message.name === "message:read" ||
                  message.data?.type === "message:read"
                ) {
                  // Refetch internal chat list and detail with retry
                  await retryWithBackoff(async () => {
                    await Promise.all([
                      globalQueryClient.refetchQueries({
                        queryKey: queryKeys.ping.internalChats.all(),
                      }),
                      globalQueryClient.refetchQueries({
                        queryKey: queryKeys.ping.internalChats.detail(chatId),
                      }),
                    ]);
                  });
                  return;
                }

                // For new messages, refetch everything with retry
                await retryWithBackoff(async () => {
                  await Promise.all([
                    // Refetch infinite messages query
                    globalQueryClient.refetchQueries({
                      queryKey:
                        queryKeys.ping.internalMessages.infinite(chatId),
                      exact: false, // Match all pageSize variants
                    }),
                    // Refetch internal chat list
                    globalQueryClient.refetchQueries({
                      queryKey: queryKeys.ping.internalChats.all(),
                    }),
                    // Refetch internal chat detail
                    globalQueryClient.refetchQueries({
                      queryKey: queryKeys.ping.internalChats.detail(chatId),
                    }),
                  ]);
                });

                // Call context-specific callback after successful refetch
                if (callback) {
                  callback();
                }
              } finally {
                // Clean up active refetch tracking
                activeRefetches.delete(debounceKey);
              }
            })();

            // Track active refetch
            activeRefetches.set(debounceKey, refetchPromise);
            await refetchPromise;
          } catch (error) {
            console.error(
              `Error handling message for internal chat ${chatId}:`,
              error
            );
            // Clean up on error
            activeRefetches.delete(debounceKey);
          } finally {
            refetchDebounceMap.delete(debounceKey);
          }
        }, REFETCH_DEBOUNCE_MS);

        refetchDebounceMap.set(debounceKey, debounceTimeout);
      } catch (error) {
        console.error(
          `Error in message handler for internal chat ${chatId}:`,
          error
        );
      }
    };

    // Subscribe to new_message event (internal chats use 'new_message')
    subscription!.channel.subscribe("new_message", handleMessage);

    // Mark this handler as registered
    subscription.handlers.add(handlerId);

    return subscription.channel;
  } catch (error) {
    console.error(`Error subscribing to internal chat ${chatId}:`, error);
    throw error;
  }
};

// Unsubscribe from a specific internal chat context
export const unsubscribeFromInternalChat = (
  chatId: string,
  context?: SubscriptionContext
) => {
  const subscription = activeSubscriptions.get(`internal_${chatId}`);
  if (!subscription) return;

  if (context) {
    // Remove specific context handler
    const handlerId = `${context}_${chatId}`;
    subscription.handlers.delete(handlerId);

    // If no more handlers, unsubscribe and remove
    if (subscription.handlers.size === 0) {
      subscription.channel.unsubscribe();
      activeSubscriptions.delete(`internal_${chatId}`);
    }
  } else {
    // Unsubscribe entirely from this chat
    subscription.channel.unsubscribe();
    activeSubscriptions.delete(`internal_${chatId}`);
  }
};

// Publish typing indicator (supports both chat types)
export const publishTypingIndicator = (
  chatId: string,
  isTyping: boolean,
  isInternal: boolean = false
) => {
  try {
    const channelName = isInternal
      ? `internal-chat:${chatId}`
      : `chat:${chatId}`;
    const channel = ably.channels.get(channelName);
    channel.publish("typing", {
      chatId,
      isTyping,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error(
      `Error publishing typing indicator to ${isInternal ? "internal " : ""}chat ${chatId}:`,
      error
    );
  }
};

// Publish message read status (supports both chat types)
export const publishMessageRead = (
  chatId: string,
  isInternal: boolean = false
) => {
  try {
    const channelName = isInternal
      ? `internal-chat:${chatId}`
      : `chat:${chatId}`;
    const channel = ably.channels.get(channelName);
    channel.publish("message:read", {
      chatId,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error(
      `Error publishing message read to ${isInternal ? "internal " : ""}chat ${chatId}:`,
      error
    );
  }
};

// Get connection state
export const getConnectionState = (): ConnectionState => connectionState;

// Check if connected
export const isConnected = (): boolean => connectionState.isConnected;

// Manually reconnect
export const reconnect = () => {
  if (!connectionState.isConnected && !connectionState.isConnecting) {
    ably.connection.connect();
  }
};

// Wait for connection before subscribing
export const waitForConnection = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (connectionState.isConnected) {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Connection timeout"));
    }, 10000);

    const cleanup = addConnectionListener((state) => {
      if (state.isConnected) {
        clearTimeout(timeout);
        cleanup();
        resolve();
      } else if (state.error && !state.isConnecting) {
        clearTimeout(timeout);
        cleanup();
        reject(new Error(state.error));
      }
    });
  });
};

// Initialize Ably connection
export const initializeAbly = async (): Promise<void> => {
  try {
    if (!connectionState.isConnected && !connectionState.isConnecting) {
      ably.connection.connect();
      await waitForConnection();
    }
  } catch (error) {
    console.error("Failed to initialize Ably:", error);
    throw error;
  }
};
